import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dashboard from './Dashboard';
import { useNavigate } from 'react-router-dom';
import './VendorPortfolio.css';
import Chart from 'chart.js/auto';

const apiUrl = process.env.REACT_APP_API_URL || 'https://vakra.co.in/api';

const emptyDashboard = {
  summary: {
    todaySales: 0,
    todayOrders: 0,
    pendingPayments: 0,
    customerDue: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    returnsCount: 0,
    todayProfit: 0,
  },
  sales: {
    daily: { total_sales: 0, order_count: 0, sale_date: null },
    weekly: { total_sales: 0, order_count: 0, sale_week: null },
    monthly: { total_sales: 0, order_count: 0, sale_month: null },
    yearly: { total_sales: 0, order_count: 0, sale_year: null },
    total: { total_sales: 0, order_count: 0 },
  },
  paymentSummary: {
    paid: 0,
    pending: 0,
    cod: 0,
    refunded: 0,
  },
  inventorySummary: {
    totalProducts: 0,
    totalVariants: 0,
    availableStock: 0,
    reservedStock: 0,
    soldStock: 0,
    lowStock: 0,
    outOfStock: 0,
    damagedStock: 0,
    note: '',
  },
  customerCreditSummary: {
    totalDue: 0,
    totalCustomersWithDue: 0,
  },
  orderTypeDistribution: {
    individual: 0,
    group: 0,
    family: 0,
  },
  offerFrequency: [],
  topProducts: [],
  recentOrders: [],
  futureSelections: [],
  allCustomers: [],
};

const StandardVendorPortfolio = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [dashboardData, setDashboardData] = useState(emptyDashboard);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [customerFilter, setCustomerFilter] = useState('');
  const [showAllCustomers, setShowAllCustomers] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const salesChartRef = useRef(null);
  const orderTypeChartRef = useRef(null);
  const offerChartRef = useRef(null);

  const salesChartInstance = useRef(null);
  const orderTypeChartInstance = useRef(null);
  const offerChartInstance = useRef(null);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    fetchDashboard();

    return () => {
      if (salesChartInstance.current) salesChartInstance.current.destroy();
      if (orderTypeChartInstance.current) orderTypeChartInstance.current.destroy();
      if (offerChartInstance.current) offerChartInstance.current.destroy();
    };
  }, [token, navigate]);

  useEffect(() => {
    if (!loading) {
      renderCharts();
    }
  }, [dashboardData, loading]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${apiUrl}/vendor/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to load dashboard');
      }

      const data = await response.json();
      setDashboardData({
        ...emptyDashboard,
        ...data,
        summary: { ...emptyDashboard.summary, ...(data.summary || {}) },
        sales: { ...emptyDashboard.sales, ...(data.sales || {}) },
        paymentSummary: { ...emptyDashboard.paymentSummary, ...(data.paymentSummary || {}) },
        inventorySummary: { ...emptyDashboard.inventorySummary, ...(data.inventorySummary || {}) },
        customerCreditSummary: { ...emptyDashboard.customerCreditSummary, ...(data.customerCreditSummary || {}) },
        orderTypeDistribution: { ...emptyDashboard.orderTypeDistribution, ...(data.orderTypeDistribution || {}) },
        offerFrequency: Array.isArray(data.offerFrequency) ? data.offerFrequency : [],
        topProducts: Array.isArray(data.topProducts) ? data.topProducts : [],
        recentOrders: Array.isArray(data.recentOrders) ? data.recentOrders : [],
        futureSelections: Array.isArray(data.futureSelections) ? data.futureSelections : [],
        allCustomers: Array.isArray(data.allCustomers) ? data.allCustomers : [],
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Dashboard data load nahi hua. Backend/API check karo.');
    } finally {
      setLoading(false);
    }
  };

  const renderCharts = () => {
    const { sales, orderTypeDistribution, offerFrequency } = dashboardData;

    if (salesChartInstance.current) salesChartInstance.current.destroy();
    if (orderTypeChartInstance.current) orderTypeChartInstance.current.destroy();
    if (offerChartInstance.current) offerChartInstance.current.destroy();

    if (salesChartRef.current) {
      salesChartInstance.current = new Chart(salesChartRef.current, {
        type: 'bar',
        data: {
          labels: ['Daily', 'Weekly', 'Monthly', 'Yearly', 'Total'],
          datasets: [
            {
              label: 'Sales (₹)',
              data: [
                Number(sales?.daily?.total_sales || 0),
                Number(sales?.weekly?.total_sales || 0),
                Number(sales?.monthly?.total_sales || 0),
                Number(sales?.yearly?.total_sales || 0),
                Number(sales?.total?.total_sales || 0),
              ],
              backgroundColor: ['#16a34a', '#2563eb', '#9333ea', '#ea580c', '#475569'],
              borderRadius: 8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'top' } },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: 'Sales (₹)' },
            },
          },
        },
      });
    }

    if (orderTypeChartRef.current) {
      orderTypeChartInstance.current = new Chart(orderTypeChartRef.current, {
        type: 'doughnut',
        data: {
          labels: ['Individual', 'Group', 'Family'],
          datasets: [
            {
              data: [
                Number(orderTypeDistribution?.individual || 0),
                Number(orderTypeDistribution?.group || 0),
                Number(orderTypeDistribution?.family || 0),
              ],
              backgroundColor: ['#f43f5e', '#3b82f6', '#f59e0b'],
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'top' } },
        },
      });
    }

    if (offerChartRef.current) {
      const labels = offerFrequency.length > 0
        ? offerFrequency.map((x) => x.offer_name || 'No Offer')
        : ['No Data'];

      const values = offerFrequency.length > 0
        ? offerFrequency.map((x) => Number(x.count || 0))
        : [0];

      offerChartInstance.current = new Chart(offerChartRef.current, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Offer Frequency',
              data: values,
              backgroundColor: '#22c55e',
              borderRadius: 8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: 'Count' },
            },
          },
        },
      });
    }
  };

  const exportToCSV = async () => {
    if (!fromDate || !toDate) {
      setError('Please select both From Date and To Date.');
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      setError('From Date hamesha To Date se chhoti ya equal honi chahiye.');
      return;
    }

    try {
      setError('');
      const response = await fetch(
        `${apiUrl}/vendor/orders/range?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt || 'CSV export failed');
      }

      const orders = await response.json();

      if (!Array.isArray(orders) || orders.length === 0) {
        setError('Selected range me koi order data nahi mila.');
        return;
      }

      let csv = 'Order ID,Date,Time,Customer,Payment Status,Payment Method,Mode,Product,Price,Qty,Item Total,Offer,Order Total\n';
      const productTotals = {};

      orders.forEach((order) => {
        let products = [];
        try {
          products = typeof order.products === 'string' ? JSON.parse(order.products) : order.products || [];
        } catch {
          products = [];
        }

        const dateObj = new Date(order.created_at);
        const date = isNaN(dateObj.getTime()) ? '' : dateObj.toLocaleDateString('en-IN');
        const time = isNaN(dateObj.getTime()) ? '' : dateObj.toLocaleTimeString('en-IN');

        if (products.length === 0) {
          csv += `${order.id},${date},${time},${order.customer_number || ''},${order.payment_status || ''},${order.payment_method || ''},${order.order_mode || ''},"No Items",0,0,0,"${order.offer_name || 'None'}",${order.total_price || 0}\n`;
        } else {
          products.forEach((p) => {
            const price = parseFloat(p.price || 0);
            const qty = Number(p.quantity || 1);
            const total = price * qty;
            const name = p.name || p.product_name || 'Unknown';

            csv += `${order.id},${date},${time},${order.customer_number || ''},${order.payment_status || ''},${order.payment_method || ''},${order.order_mode || ''},"${name}",${price},${qty},${total},"${order.offer_name || 'None'}",${order.total_price || 0}\n`;

            if (!productTotals[name]) productTotals[name] = { qty: 0, total: 0 };
            productTotals[name].qty += qty;
            productTotals[name].total += total;
          });
        }
      });

      csv += '\n\nProduct Name,Total Qty,Total Sales\n';
      Object.keys(productTotals).forEach((name) => {
        csv += `"${name}",${productTotals[name].qty},${productTotals[name].total.toFixed(2)}\n`;
      });

      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Vendor_Report_${fromDate}_to_${toDate}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('CSV export error:', err);
      setError('CSV export fail ho gaya.');
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!customerFilter.trim()) return [];
    const query = customerFilter.trim();
    return dashboardData.allCustomers.filter((c) => String(c.customer_number || '').includes(query));
  }, [dashboardData.allCustomers, customerFilter]);

  const selectedCustomerStats = useMemo(() => {
    if (filteredCustomers.length === 0) {
      return {
        customer_number: '',
        visit_count: 0,
        total_purchase: 0,
        offer_count: 0,
        due_balance: 0,
      };
    }
    return filteredCustomers[0];
  }, [filteredCustomers]);

  const StatCard = ({ title, value, subValue, colorClass = 'bg-white' }) => (
    <div className={`${colorClass} rounded-xl border p-4 shadow-sm`}>
      <p className="text-sm font-medium text-gray-600">{title}</p>
      <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
      {subValue ? <p className="mt-1 text-xs text-gray-500">{subValue}</p> : null}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <Dashboard />

      <header className="border-b bg-white px-4 py-4 shadow-sm">
        <div className="mx-auto max-w-7xl">
          <h1 className="text-2xl font-bold text-gray-900">Vendor Business Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Sales, payments, inventory, customer due, future selections aur recent orders ek hi screen par.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        {error ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-xl border bg-white p-8 text-center shadow-sm">
            <p className="text-lg font-semibold text-gray-700">Dashboard loading...</p>
          </div>
        ) : (
          <>
            <section className="mb-6">
              <h2 className="mb-3 text-xl font-semibold text-gray-900">Business Summary</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <StatCard title="Today Sales" value={`₹${Number(dashboardData.summary.todaySales || 0).toFixed(2)}`} colorClass="bg-green-50" />
                <StatCard title="Today Orders" value={dashboardData.summary.todayOrders || 0} colorClass="bg-blue-50" />
                <StatCard title="Pending Payments" value={`₹${Number(dashboardData.summary.pendingPayments || 0).toFixed(2)}`} colorClass="bg-yellow-50" />
                <StatCard title="Customer Due" value={`₹${Number(dashboardData.summary.customerDue || 0).toFixed(2)}`} colorClass="bg-red-50" />
                <StatCard title="Low Stock Items" value={dashboardData.summary.lowStockItems || 0} colorClass="bg-orange-50" />
                <StatCard title="Out of Stock" value={dashboardData.summary.outOfStockItems || 0} colorClass="bg-rose-50" />
                <StatCard title="Returns Count" value={dashboardData.summary.returnsCount || 0} colorClass="bg-purple-50" />
                <StatCard title="Estimated Profit Today" value={`₹${Number(dashboardData.summary.todayProfit || 0).toFixed(2)}`} colorClass="bg-emerald-50" />
              </div>
            </section>

            <section className="mb-6">
              <h2 className="mb-3 text-xl font-semibold text-gray-900">Inventory & Payment Snapshot</h2>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">Inventory Snapshot</h3>

                  {dashboardData.inventorySummary.note ? (
                    <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                      {dashboardData.inventorySummary.note}
                    </div>
                  ) : null}

                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <StatCard title="Products" value={dashboardData.inventorySummary.totalProducts || 0} />
                    <StatCard title="Variants" value={dashboardData.inventorySummary.totalVariants || 0} />
                    <StatCard title="Available" value={dashboardData.inventorySummary.availableStock || 0} />
                    <StatCard title="Reserved" value={dashboardData.inventorySummary.reservedStock || 0} />
                    <StatCard title="Sold" value={dashboardData.inventorySummary.soldStock || 0} />
                    <StatCard title="Low Stock" value={dashboardData.inventorySummary.lowStock || 0} />
                    <StatCard title="Out of Stock" value={dashboardData.inventorySummary.outOfStock || 0} />
                    <StatCard title="Damaged" value={dashboardData.inventorySummary.damagedStock || 0} />
                  </div>
                </div>

                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">Payment Snapshot</h3>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <StatCard title="Paid" value={`₹${Number(dashboardData.paymentSummary.paid || 0).toFixed(2)}`} colorClass="bg-green-50" />
                    <StatCard title="Pending" value={`₹${Number(dashboardData.paymentSummary.pending || 0).toFixed(2)}`} colorClass="bg-yellow-50" />
                    <StatCard title="COD" value={`₹${Number(dashboardData.paymentSummary.cod || 0).toFixed(2)}`} colorClass="bg-blue-50" />
                    <StatCard title="Refunded" value={`₹${Number(dashboardData.paymentSummary.refunded || 0).toFixed(2)}`} colorClass="bg-red-50" />
                  </div>

                  <div className="mt-4 rounded-lg border bg-slate-50 p-3">
                    <p className="text-sm font-medium text-gray-700">Customer Credit / Due Summary</p>
                    <p className="mt-2 text-xl font-bold text-gray-900">
                      ₹{Number(dashboardData.customerCreditSummary.totalDue || 0).toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {dashboardData.customerCreditSummary.totalCustomersWithDue || 0} customers ke upar due hai
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
              <div className="rounded-xl border bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-lg font-semibold text-gray-800">Sales Trends</h3>
                <div style={{ height: 300 }}>
                  <canvas ref={salesChartRef}></canvas>
                </div>
              </div>

              <div className="rounded-xl border bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-lg font-semibold text-gray-800">Order Type Distribution</h3>
                <div style={{ height: 300 }}>
                  <canvas ref={orderTypeChartRef}></canvas>
                </div>
              </div>

              <div className="rounded-xl border bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-lg font-semibold text-gray-800">Offer Frequency</h3>
                <div style={{ height: 300 }}>
                  <canvas ref={offerChartRef}></canvas>
                </div>
              </div>
            </section>

            <section className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-xl border bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-lg font-semibold text-gray-800">Top Selling Products</h3>
                {dashboardData.topProducts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100 text-left text-sm">
                          <th className="border px-3 py-2">Product</th>
                          <th className="border px-3 py-2">Qty Sold</th>
                          <th className="border px-3 py-2">Sales</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.topProducts.map((item, idx) => (
                          <tr key={`${item.product_name}-${idx}`} className="text-sm">
                            <td className="border px-3 py-2">{item.product_name}</td>
                            <td className="border px-3 py-2">{item.qty_sold}</td>
                            <td className="border px-3 py-2">₹{Number(item.total_sales || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Top product data available nahi hai.</p>
                )}
              </div>

              <div className="rounded-xl border bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-lg font-semibold text-gray-800">Future Preferences</h3>
                {dashboardData.futureSelections.length > 0 ? (
                  <div className="max-h-80 overflow-auto">
                    <table className="min-w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100 text-left text-sm">
                          <th className="border px-3 py-2">Customer</th>
                          <th className="border px-3 py-2">Image</th>
                          <th className="border px-3 py-2">Price</th>
                          <th className="border px-3 py-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.futureSelections.map((selection) => (
                          <tr key={selection.id} className="text-sm">
                            <td className="border px-3 py-2">{selection.customer_number}</td>
                            <td className="border px-3 py-2">
                              <img
                                src={selection.product_image}
                                alt="Product"
                                style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </td>
                            <td className="border px-3 py-2">₹{Number(selection.price || 0).toFixed(2)}</td>
                            <td className="border px-3 py-2">
                              {selection.selection_date ? new Date(selection.selection_date).toLocaleDateString('en-IN') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Future preferences nahi mile.</p>
                )}
              </div>
            </section>

            <section className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-xl border bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-lg font-semibold text-gray-800">Recent Orders</h3>
                {dashboardData.recentOrders.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100 text-left text-sm">
                          <th className="border px-3 py-2">Order ID</th>
                          <th className="border px-3 py-2">Customer</th>
                          <th className="border px-3 py-2">Mode</th>
                          <th className="border px-3 py-2">Payment</th>
                          <th className="border px-3 py-2">Total</th>
                          <th className="border px-3 py-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.recentOrders.map((order) => (
                          <tr key={order.id} className="text-sm">
                            <td className="border px-3 py-2">#{order.id}</td>
                            <td className="border px-3 py-2">{order.customer_number || '—'}</td>
                            <td className="border px-3 py-2">{order.order_mode || '—'}</td>
                            <td className="border px-3 py-2">
                              {order.payment_status || '—'}
                              {order.payment_method ? ` / ${order.payment_method}` : ''}
                            </td>
                            <td className="border px-3 py-2">₹{Number(order.total_price || 0).toFixed(2)}</td>
                            <td className="border px-3 py-2">
                              {order.created_at ? new Date(order.created_at).toLocaleString('en-IN') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Recent orders nahi mile.</p>
                )}
              </div>

              <div className="rounded-xl border bg-white p-4 shadow-sm">
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">Customer Search / Credit / Visits</h3>
                  <button
                    onClick={() => setShowAllCustomers((prev) => !prev)}
                    className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
                  >
                    {showAllCustomers ? 'Hide All Customers' : 'Show All Customers'}
                  </button>
                </div>

                <input
                  type="text"
                  placeholder="Customer number search karo"
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  className="mb-4 w-full rounded-lg border px-3 py-2"
                />

                {customerFilter.trim() ? (
                  filteredCustomers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-100 text-left text-sm">
                            <th className="border px-3 py-2">Customer</th>
                            <th className="border px-3 py-2">Visits</th>
                            <th className="border px-3 py-2">Purchase</th>
                            <th className="border px-3 py-2">Offers</th>
                            <th className="border px-3 py-2">Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="text-sm">
                            <td className="border px-3 py-2">{selectedCustomerStats.customer_number}</td>
                            <td className="border px-3 py-2">{selectedCustomerStats.visit_count}</td>
                            <td className="border px-3 py-2">₹{Number(selectedCustomerStats.total_purchase || 0).toFixed(2)}</td>
                            <td className="border px-3 py-2">{selectedCustomerStats.offer_count}</td>
                            <td className="border px-3 py-2">₹{Number(selectedCustomerStats.due_balance || 0).toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Is customer ke liye data nahi mila.</p>
                  )
                ) : (
                  <p className="text-sm text-gray-500">Customer number enter karo to stats dikhेंगे.</p>
                )}

                {showAllCustomers && (
                  <div className="mt-4 max-h-56 overflow-auto rounded-lg border bg-slate-50 p-3">
                    <h4 className="mb-2 font-semibold text-gray-800">All Customers</h4>
                    {dashboardData.allCustomers.length > 0 ? (
                      <ul className="space-y-2 text-sm text-gray-700">
                        {dashboardData.allCustomers.map((c, idx) => (
                          <li key={`${c.customer_number}-${idx}`} className="rounded border bg-white px-3 py-2">
                            {c.customer_number} — Visits: {c.visit_count} — Due: ₹{Number(c.due_balance || 0).toFixed(2)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">Customers list empty hai.</p>
                    )}
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-xl border bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-xl font-bold text-gray-900">Export Sales Report (CSV)</h2>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
                <div className="flex-1">
                  <label className="mb-2 block text-sm font-semibold text-gray-700">From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full rounded-lg border px-3 py-3"
                  />
                </div>
                <div className="flex-1">
                  <label className="mb-2 block text-sm font-semibold text-gray-700">To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full rounded-lg border px-3 py-3"
                  />
                </div>
                <div>
                  <button
                    onClick={exportToCSV}
                    className="rounded-lg bg-green-700 px-6 py-3 font-bold text-white hover:bg-green-800"
                  >
                    Download CSV Report
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default StandardVendorPortfolio;
